import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, ArrowRight, Download, LayoutList } from 'lucide-react';

const AttendanceManagement = ({ basePath = '/admin/attendance' }) => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const attendanceOptions = [
    {
      title: 'Date-wise Attendance',
      description: 'View attendance records organized by date. Browse through calendar and check who was present on specific days.',
      icon: Calendar,
      gradient: 'from-cyan-400 via-blue-500 to-indigo-600',
      glowColor: 'shadow-cyan-500/50',
      iconBg: 'from-cyan-100 to-blue-100',
      route: `${basePath}/date-wise`
    },
    {
      title: 'Employee-wise Attendance',
      description: 'View attendance records organized by employee. Track individual attendance history and patterns.',
      icon: Users,
      gradient: 'from-purple-400 via-pink-500 to-rose-600',
      glowColor: 'shadow-purple-500/50',
      iconBg: 'from-purple-100 to-pink-100',
      route: `${basePath}/employee-wise`
    },
    {
      title: 'Download Reports',
      description: 'Generate and download monthly attendance reports (PDF) for any employee with detailed analytics.',
      icon: Download,
      gradient: 'from-emerald-400 via-teal-500 to-cyan-600',
      glowColor: 'shadow-emerald-500/50',
      iconBg: 'from-emerald-100 to-teal-100',
      route: `${basePath}/employee-wise#download`
    },
    {
      title: 'Attendance Overview',
      description: 'Monthly consolidated Full Days, Half Days, and Absent counts for all employees — for quick attendance reporting.',
      icon: LayoutList,
      gradient: 'from-slate-500 via-slate-600 to-slate-800',
      glowColor: 'shadow-slate-500/50',
      iconBg: 'from-slate-100 to-slate-200',
      route: `${basePath}/overview`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Floating orbs background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header with sparkle effect */}
        <div className="text-center mb-16">
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 bg-clip-text text-transparent">
              Attendance Management
            </span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Choose your preferred view to manage and track attendance effortlessly
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {attendanceOptions.map((option, index) => {
            const Icon = option.icon;
            const isHovered = hoveredCard === index;
            
            return (
              <div
                key={index}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => navigate(option.route)}
                className="group cursor-pointer"
              >
                <div className={`
                  relative bg-white rounded-3xl overflow-hidden
                  transition-all duration-500 ease-out
                  ${isHovered ? `shadow-2xl ${option.glowColor} scale-105` : 'shadow-xl'}
                `}>
                  {/* Gradient overlay on hover */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-br ${option.gradient} opacity-0 
                    transition-opacity duration-500
                    ${isHovered ? 'opacity-5' : ''}
                  `}></div>

                  {/* Animated border */}
                  <div className={`
                    absolute inset-0 rounded-3xl bg-gradient-to-r ${option.gradient}
                    transition-all duration-500
                    ${isHovered ? 'opacity-100 p-[2px]' : 'opacity-0 p-0'}
                  `}>
                    <div className="w-full h-full bg-white rounded-3xl"></div>
                  </div>

                  <div className="relative p-8">
                    {/* Icon with gradient background */}
                    <div className={`
                      inline-flex p-4 rounded-2xl bg-gradient-to-br ${option.iconBg} mb-6
                      transition-all duration-500
                      ${isHovered ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}
                    `}>
                      <Icon className={`w-8 h-8 text-slate-700`} 
                        strokeWidth={2.5}
                      />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {option.title}
                    </h3>

                    {/* Description */}
                    <p className="text-slate-600 leading-relaxed mb-6 min-h-[4rem]">
                      {option.description}
                    </p>

                    {/* Action button */}
                    <div className={`
                      flex items-center justify-between p-4 rounded-xl
                      bg-gradient-to-r ${option.gradient}
                      transition-all duration-300
                      ${isHovered ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-90'}
                    `}>
                      <span className="text-white font-semibold">
                        {isHovered ? 'Click to explore' : 'View details'}
                      </span>
                      <ArrowRight className={`
                        w-5 h-5 text-white transition-transform duration-300
                        ${isHovered ? 'translate-x-2' : 'translate-x-0'}
                      `} />
                    </div>
                  </div>

                  {/* Decorative corner elements */}
                  <div className={`
                    absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${option.gradient} 
                    rounded-bl-full opacity-5
                    transition-all duration-500
                    ${isHovered ? 'scale-150' : 'scale-100'}
                  `}></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section with glassmorphism */}
        <div className="relative bg-white/40 backdrop-blur-xl rounded-3xl p-8 border border-white/60 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl"></div>
          
          <div className="relative flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-3">
                💡 Quick Tip
              </h3>
              <p className="text-slate-700 text-m leading-relaxed">
                Use <span className="font-bold text-cyan-600">Date-wise</span> view to quickly check attendance for specific days or monitor daily patterns. 
                Use <span className="font-bold text-purple-600">Employee-wise</span> view to track individual employee attendance history and performance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagement;